/**
 * Task #1763 — Pagina admin master (4 tab: utenti master, ruoli, permessi,
 * assegnazioni). Consuma le rotte tipizzate del Task #1762:
 *   GET/POST /api/master/master-users
 *   PATCH/DELETE /api/master/master-users/:userId (+ reset-password, disable-2fa, revoke-sessions)
 *   GET/POST/PATCH/DELETE /api/master/master-roles
 *   GET /api/master/master-permissions/catalog
 *   GET/PUT /api/master/master-permissions/role/:roleId
 *
 * Out of scope: wizard 2FA self-service master (Task #1764).
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Alert, AlertDescription } from '@easyflow/ui-master/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-master/components/ui/tabs';
import { Textarea } from '@easyflow/ui-master/components/ui/textarea';
import { Checkbox } from '@easyflow/ui-master/components/ui/checkbox';
import { Switch } from '@easyflow/ui-master/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-master/components/ui/dialog';
import {
  Users, ShieldCheck, KeyRound, Link2, Plus, Edit, Trash2, Search,
  AlertCircle, CheckCircle, Key, ShieldOff, LogOut, Eye,
} from 'lucide-react';
import { SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { UserAvatar } from '@easyflow/ui-master/components/ui/user-avatar';
import { RoleBadge } from '@easyflow/ui-master/components/ui/role-badge';
import { TwoFactorStatusBadge } from '@easyflow/ui-master/components/ui/two-factor-status-badge';
import { ConfirmActionModal } from '@easyflow/ui-master/components/ui/confirm-action-modal';
import { SessionRow } from '@easyflow/ui-master/components/ui/session-row';
import apiClient from '@/lib/api';
import type {
  MasterUserListItem,
  MasterUserListFilters,
  MasterUserDetail,
  MasterUserCreateRequest,
  MasterUserUpdateRequest,
  MasterUserRole,
  MasterUserStatus,
  MasterRole,
  MasterRoleDetail,
  MasterPermission,
  MasterPermissionCatalog,
  MasterRefreshTokenInfo,
  MasterTwoFactorRequirementResponse,
} from '@easyflow/shared';

const PAGE_SIZE = 50;
const ROLE_OPTIONS: Array<MasterUserRole | ''> = ['', 'super_admin', 'admin', 'manager', 'agent', 'user'];
const STATUS_OPTIONS: Array<MasterUserStatus | ''> = ['', 'active', 'inactive', 'locked', 'pending_verification'];

type DestructiveActionKind = 'reset-password' | 'disable-2fa' | 'revoke-sessions' | 'delete-user';

interface MasterRoleListItem extends MasterRole { user_count: number }

interface CreateUserForm {
  email: string;
  first_name: string;
  last_name: string;
  role_id: string;
  status: MasterUserStatus;
  mobile: string;
}

interface RoleForm {
  name: string;
  description: string;
  role_kind: string;
  permission_ids: string[];
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return 'Mai';
  try {
    return new Date(iso).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

const EMPTY_CREATE_FORM: CreateUserForm = {
  email: '', first_name: '', last_name: '',
  role_id: '', status: 'active', mobile: '',
};

const EMPTY_ROLE_FORM: RoleForm = {
  name: '', description: '', role_kind: '', permission_ids: [],
};

export default function MasterUsersMaster(): JSX.Element {
  const [activeTab, setActiveTab] = useState<'users' | 'roles' | 'permissions' | 'assignments'>('users');
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // ------------------ MASTER USERS ------------------
  const [users, setUsers] = useState<MasterUserListItem[]>([]);
  const [usersTotal, setUsersTotal] = useState<number>(0);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersSearch, setUsersSearch] = useState('');
  const [filterRole, setFilterRole] = useState<MasterUserRole | ''>('');
  const [filterStatus, setFilterStatus] = useState<MasterUserStatus | ''>('');
  const [filterTotp, setFilterTotp] = useState<'' | 'true' | 'false'>('');
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [twoFactorRequired, setTwoFactorRequired] = useState<boolean>(false);
  const [twoFaReqLoading, setTwoFaReqLoading] = useState<boolean>(true);
  const [twoFaReqSaving, setTwoFaReqSaving] = useState<boolean>(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedUserDetail, setSelectedUserDetail] = useState<MasterUserDetail | null>(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);

  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>(EMPTY_CREATE_FORM);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [editUserTarget, setEditUserTarget] = useState<MasterUserListItem | null>(null);
  const [editUserForm, setEditUserForm] = useState<MasterUserUpdateRequest>({});
  const [editUserSubmitting, setEditUserSubmitting] = useState(false);
  const [editUserError, setEditUserError] = useState<string | null>(null);

  const [destructiveAction, setDestructiveAction] = useState<{
    kind: DestructiveActionKind;
    user: MasterUserListItem | MasterUserDetail;
  } | null>(null);
  const [destructiveSubmitting, setDestructiveSubmitting] = useState(false);
  const [destructiveError, setDestructiveError] = useState<string | null>(null);

  // ------------------ MASTER ROLES ------------------
  const [roles, setRoles] = useState<MasterRoleListItem[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [selectedRoleDetail, setSelectedRoleDetail] = useState<MasterRoleDetail | null>(null);
  const [roleDetailLoading, setRoleDetailLoading] = useState(false);

  const [showRoleEditor, setShowRoleEditor] = useState<null | { mode: 'create' } | { mode: 'edit'; role: MasterRoleListItem }>(null);
  const [roleForm, setRoleForm] = useState<RoleForm>(EMPTY_ROLE_FORM);
  const [roleSubmitting, setRoleSubmitting] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  const [deleteRoleTarget, setDeleteRoleTarget] = useState<MasterRoleListItem | null>(null);
  const [deleteRoleSubmitting, setDeleteRoleSubmitting] = useState(false);
  const [deleteRoleError, setDeleteRoleError] = useState<string | null>(null);

  // ------------------ PERMISSIONS ------------------
  const [catalog, setCatalog] = useState<MasterPermissionCatalog | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [permSearch, setPermSearch] = useState('');

  // ------------------ ASSIGNMENTS ------------------
  const [assignmentRoleId, setAssignmentRoleId] = useState<string>('');
  const [assignmentSelectedIds, setAssignmentSelectedIds] = useState<Set<string>>(new Set());
  const [assignmentInitialIds, setAssignmentInitialIds] = useState<Set<string>>(new Set());
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentSubmitting, setAssignmentSubmitting] = useState(false);
  const [assignmentConfirmOpen, setAssignmentConfirmOpen] = useState(false);

  // ------------------ EFFECTS ------------------
  useEffect(() => { void loadRoles(); void loadCatalog(); void loadTwoFaRequirement(); }, []);
  useEffect(() => {
    void loadUsers();
  }, [filterRole, filterStatus, filterTotp, pageOffset]);
  // Debounce ricerca utenti master.
  useEffect(() => {
    const t = window.setTimeout(() => { setPageOffset(0); void loadUsers(); }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
  }, [usersSearch]);
  useEffect(() => {
    if (!selectedUserId) { setSelectedUserDetail(null); return; }
    void loadUserDetail(selectedUserId);
  }, [selectedUserId]);
  useEffect(() => {
    if (!selectedRoleId) { setSelectedRoleDetail(null); return; }
    void loadRoleDetail(selectedRoleId);
  }, [selectedRoleId]);
  useEffect(() => {
    if (!assignmentRoleId) {
      setAssignmentSelectedIds(new Set()); setAssignmentInitialIds(new Set()); return;
    }
    void loadRoleAssignments(assignmentRoleId);
  }, [assignmentRoleId]);

  // Mappa role_id → nome ruolo reale (master.roles) per badge/label unificati.
  const roleNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of roles) m.set(r.id, r.name);
    return m;
  }, [roles]);
  // Ruolo da mostrare: il ruolo granulare reale se presente, altrimenti il legacy.
  function displayRole(roleId: string | null, legacy: MasterUserRole): string {
    return (roleId ? roleNameById.get(roleId) : undefined) ?? legacy;
  }
  // Il campo legacy `role` serve SOLO al bypass super_admin: derivato dal ruolo reale.
  // Ritorna null se il ruolo non è risolvibile (ruoli non ancora caricati o id ignoto)
  // così il submit aborta invece di degradare silenziosamente a 'user' e perdere il bypass.
  function deriveLegacyRole(roleId: string): MasterUserRole | null {
    const name = roleNameById.get(roleId);
    if (!name) return null;
    return name === 'super_admin' ? 'super_admin' : 'user';
  }

  function flashSuccess(msg: string): void {
    setSuccessMessage(msg);
    window.setTimeout(() => setSuccessMessage(''), 3500);
  }

  async function loadTwoFaRequirement(): Promise<void> {
    try {
      setTwoFaReqLoading(true);
      const resp = await apiClient.get<MasterTwoFactorRequirementResponse>('/master/2fa-requirement');
      if (resp.success && resp.data) {
        setTwoFactorRequired(resp.data.twoFactorRequired);
      }
    } catch {
      // Lettura best-effort: in caso di errore lo switch resta sul default OFF.
    } finally {
      setTwoFaReqLoading(false);
    }
  }

  async function handleToggleTwoFaRequirement(next: boolean): Promise<void> {
    try {
      setTwoFaReqSaving(true);
      const resp = await apiClient.post<MasterTwoFactorRequirementResponse>('/master/2fa-requirement', { enabled: next });
      if (resp.success && resp.data) {
        setTwoFactorRequired(resp.data.twoFactorRequired);
        flashSuccess(next
          ? '2FA obbligatorio attivato: gli admin master non iscritti dovranno configurarlo al prossimo accesso.'
          : '2FA obbligatorio disattivato.');
      } else {
        throw new Error(resp.error ?? 'Aggiornamento requisito 2FA fallito');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore aggiornamento requisito 2FA');
    } finally {
      setTwoFaReqSaving(false);
    }
  }

  async function loadUsers(): Promise<void> {
    try {
      setUsersLoading(true);
      const filters: MasterUserListFilters = {
        limit: PAGE_SIZE,
        offset: pageOffset,
        ...(usersSearch.trim() ? { search: usersSearch.trim() } : {}),
        ...(filterRole ? { role: filterRole } : {}),
        ...(filterStatus ? { status: filterStatus } : {}),
        ...(filterTotp ? { totp_enabled: filterTotp === 'true' } : {}),
      };
      const resp = await apiClient.listMasterUsers(filters);
      if (resp.success && resp.data) {
        setUsers(resp.data.items);
        setUsersTotal(resp.data.total);
      } else {
        throw new Error(resp.error ?? 'Caricamento utenti master fallito');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore utenti master');
    } finally { setUsersLoading(false); }
  }

  async function loadUserDetail(userId: string): Promise<void> {
    try {
      setUserDetailLoading(true);
      const resp = await apiClient.getMasterUser(userId);
      if (resp.success && resp.data) setSelectedUserDetail(resp.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore dettaglio utente');
    } finally { setUserDetailLoading(false); }
  }

  async function loadRoles(): Promise<void> {
    try {
      setRolesLoading(true);
      const resp = await apiClient.listMasterRoles();
      if (resp.success && resp.data) setRoles(resp.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore ruoli');
    } finally { setRolesLoading(false); }
  }

  async function loadRoleDetail(roleId: string): Promise<void> {
    try {
      setRoleDetailLoading(true);
      const resp = await apiClient.getMasterRole(roleId);
      if (resp.success && resp.data) setSelectedRoleDetail(resp.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore dettaglio ruolo');
    } finally { setRoleDetailLoading(false); }
  }

  async function loadCatalog(): Promise<void> {
    try {
      setCatalogLoading(true);
      const resp = await apiClient.listMasterPermissionCatalog();
      if (resp.success && resp.data) setCatalog(resp.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore catalogo permessi');
    } finally { setCatalogLoading(false); }
  }

  async function loadRoleAssignments(roleId: string): Promise<void> {
    try {
      setAssignmentLoading(true);
      const resp = await apiClient.getMasterRolePermissions(roleId);
      if (resp.success && resp.data) {
        const ids = new Set<string>(resp.data.permission_ids);
        setAssignmentSelectedIds(new Set(ids));
        setAssignmentInitialIds(ids);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore assegnazioni');
    } finally { setAssignmentLoading(false); }
  }

  // ------------------ USER ACTIONS ------------------
  function openEditUser(u: MasterUserListItem): void {
    setEditUserTarget(u);
    setEditUserForm({
      first_name: u.first_name, last_name: u.last_name,
      role_id: u.role_id ?? null, status: u.status,
    });
    setEditUserError(null);
  }

  async function submitCreateUser(): Promise<void> {
    try {
      setCreateSubmitting(true); setCreateError(null);
      const legacy = deriveLegacyRole(createForm.role_id);
      if (!legacy) { setCreateError('Ruolo non valido o ruoli non ancora caricati'); return; }
      const body: MasterUserCreateRequest = {
        email: createForm.email,
        first_name: createForm.first_name,
        last_name: createForm.last_name,
        role: legacy,
        role_id: createForm.role_id,
        status: createForm.status,
        auth_provider: 'local',
        is_local_auth_allowed: true,
        ...(createForm.mobile ? { mobile: createForm.mobile } : {}),
      };
      const resp = await apiClient.createMasterUser(body);
      if (!resp.success) throw new Error(resp.error ?? 'Creazione fallita');
      flashSuccess('Utente master creato. Email di setup password inviata.');
      setShowCreateUser(false); setCreateForm(EMPTY_CREATE_FORM);
      await loadUsers();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Creazione fallita');
    } finally { setCreateSubmitting(false); }
  }

  async function submitEditUser(): Promise<void> {
    if (!editUserTarget) return;
    try {
      setEditUserSubmitting(true); setEditUserError(null);
      const roleId = editUserForm.role_id ?? null;
      if (!roleId) { setEditUserError('Seleziona un ruolo'); return; }
      const legacy = deriveLegacyRole(roleId);
      if (!legacy) { setEditUserError('Ruolo non valido o ruoli non ancora caricati'); return; }
      const payload: MasterUserUpdateRequest = {
        ...editUserForm,
        role: legacy,
      };
      const resp = await apiClient.updateMasterUser(editUserTarget.id, payload);
      if (!resp.success) throw new Error(resp.error ?? 'Aggiornamento fallito');
      flashSuccess('Utente master aggiornato');
      setEditUserTarget(null);
      await loadUsers();
      if (selectedUserId === editUserTarget.id) await loadUserDetail(editUserTarget.id);
    } catch (e) {
      setEditUserError(e instanceof Error ? e.message : 'Aggiornamento fallito');
    } finally { setEditUserSubmitting(false); }
  }

  async function runDestructive(reason: string): Promise<void> {
    if (!destructiveAction) return;
    const { kind, user } = destructiveAction;
    try {
      setDestructiveSubmitting(true); setDestructiveError(null);
      let resp;
      if (kind === 'reset-password') {
        resp = await apiClient.resetMasterUserPassword(user.id, { reason });
        if (resp.success) flashSuccess(`Email reset password inviata a ${user.email}`);
      } else if (kind === 'disable-2fa') {
        resp = await apiClient.disableMasterUserTwoFactor(user.id, { reason });
        if (resp.success) flashSuccess('2FA disattivato per l\'utente');
      } else if (kind === 'revoke-sessions') {
        resp = await apiClient.revokeMasterUserSessions(user.id, { reason });
        if (resp.success && resp.data) flashSuccess(`Revocate ${resp.data.revoked_count} sessioni`);
      } else {
        resp = await apiClient.deleteMasterUser(user.id, { reason });
        if (resp.success) {
          flashSuccess('Utente master eliminato');
          if (selectedUserId === user.id) setSelectedUserId(null);
        }
      }
      if (!resp.success) throw new Error(resp.error ?? 'Operazione fallita');
      setDestructiveAction(null);
      await loadUsers();
      if (selectedUserId && (kind !== 'delete-user')) await loadUserDetail(selectedUserId);
    } catch (e) {
      setDestructiveError(e instanceof Error ? e.message : 'Operazione fallita');
    } finally { setDestructiveSubmitting(false); }
  }

  async function revokeSingleSession(sessionId: string): Promise<void> {
    if (!selectedUserDetail) return;
    const reason = window.prompt('Motivo revoca sessione (min 10 caratteri, audit log):');
    if (!reason || reason.trim().length < 10) return;
    try {
      const resp = await apiClient.revokeMasterUserSessions(selectedUserDetail.id, {
        reason: reason.trim(), session_id: sessionId,
      });
      if (!resp.success) throw new Error(resp.error ?? 'Revoca fallita');
      flashSuccess('Sessione revocata');
      await loadUserDetail(selectedUserDetail.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Revoca fallita');
    }
  }

  // ------------------ ROLE ACTIONS ------------------
  function openCreateRole(): void {
    setRoleForm(EMPTY_ROLE_FORM); setRoleError(null);
    setShowRoleEditor({ mode: 'create' });
  }

  function openEditRole(role: MasterRoleListItem): void {
    setRoleForm({
      name: role.name,
      description: role.description ?? '',
      role_kind: role.role_kind ?? '',
      permission_ids: [],
    });
    setRoleError(null);
    setShowRoleEditor({ mode: 'edit', role });
  }

  async function submitRole(): Promise<void> {
    if (!showRoleEditor) return;
    try {
      setRoleSubmitting(true); setRoleError(null);
      if (showRoleEditor.mode === 'create') {
        const resp = await apiClient.createMasterRole({
          name: roleForm.name,
          ...(roleForm.description ? { description: roleForm.description } : {}),
          ...(roleForm.role_kind ? { role_kind: roleForm.role_kind } : {}),
          permission_ids: roleForm.permission_ids,
        });
        if (!resp.success) throw new Error(resp.error ?? 'Creazione ruolo fallita');
        flashSuccess('Ruolo creato');
      } else {
        const resp = await apiClient.updateMasterRole(showRoleEditor.role.id, {
          name: roleForm.name,
          description: roleForm.description || null,
          role_kind: roleForm.role_kind || null,
        });
        if (!resp.success) throw new Error(resp.error ?? 'Aggiornamento ruolo fallito');
        flashSuccess('Ruolo aggiornato');
      }
      setShowRoleEditor(null);
      await loadRoles();
    } catch (e) {
      setRoleError(e instanceof Error ? e.message : 'Operazione fallita');
    } finally { setRoleSubmitting(false); }
  }

  async function submitDeleteRole(): Promise<void> {
    if (!deleteRoleTarget) return;
    try {
      setDeleteRoleSubmitting(true); setDeleteRoleError(null);
      const resp = await apiClient.deleteMasterRole(deleteRoleTarget.id);
      if (!resp.success) throw new Error(resp.error ?? 'Eliminazione fallita');
      flashSuccess('Ruolo eliminato');
      if (selectedRoleId === deleteRoleTarget.id) setSelectedRoleId(null);
      setDeleteRoleTarget(null);
      await loadRoles();
    } catch (e) {
      setDeleteRoleError(e instanceof Error ? e.message : 'Eliminazione fallita');
    } finally { setDeleteRoleSubmitting(false); }
  }

  // ------------------ ASSIGNMENT ACTIONS ------------------
  const assignmentDiff = useMemo(() => {
    const added: string[] = []; const removed: string[] = [];
    assignmentSelectedIds.forEach((id) => { if (!assignmentInitialIds.has(id)) added.push(id); });
    assignmentInitialIds.forEach((id) => { if (!assignmentSelectedIds.has(id)) removed.push(id); });
    return { added, removed };
  }, [assignmentSelectedIds, assignmentInitialIds]);

  const togglePermission = useCallback((id: string): void => {
    setAssignmentSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  async function submitAssignment(): Promise<void> {
    if (!assignmentRoleId) return;
    try {
      setAssignmentSubmitting(true);
      const resp = await apiClient.assignMasterRolePermissions(assignmentRoleId, {
        permission_ids: Array.from(assignmentSelectedIds),
        mode: 'replace',
      });
      if (!resp.success) throw new Error(resp.error ?? 'Salvataggio assegnazioni fallito');
      flashSuccess(`Assegnazioni salvate · ${resp.data?.diff.added.length ?? 0} aggiunti · ${resp.data?.diff.removed.length ?? 0} rimossi`);
      setAssignmentConfirmOpen(false);
      await loadRoleAssignments(assignmentRoleId);
      await loadRoles();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Salvataggio fallito');
      setAssignmentConfirmOpen(false);
    } finally { setAssignmentSubmitting(false); }
  }

  // ------------------ DERIVED ------------------
  const pageEnd = Math.min(pageOffset + users.length, usersTotal);
  const canPrev = pageOffset > 0;
  const canNext = pageEnd < usersTotal;

  /**
   * Risolve gli id permesso in oggetti `MasterPermission` per renderizzare la
   * preview del diff nella modale di conferma assegnazioni.
   */
  const permissionsById = useMemo(() => {
    const map = new Map<string, MasterPermission>();
    if (catalog) {
      Object.values(catalog.groups).forEach((perms) => {
        perms.forEach((p) => map.set(p.id, p));
      });
    }
    return map;
  }, [catalog]);

  const filteredCatalog = useMemo(() => {
    if (!catalog) return null;
    const q = permSearch.trim().toLowerCase();
    if (!q) return catalog;
    const groups: Record<string, MasterPermission[]> = {};
    let total = 0;
    Object.entries(catalog.groups).forEach(([cat, perms]) => {
      const filtered = perms.filter((p) =>
        p.code.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        (p.description ?? '').toLowerCase().includes(q),
      );
      if (filtered.length > 0) { groups[cat] = filtered; total += filtered.length; }
    });
    return { groups, total };
  }, [catalog, permSearch]);

  function destructiveTitle(): { title: string; description: string; cta: string; variant: 'default' | 'destructive' } {
    if (!destructiveAction) return { title: '', description: '', cta: '', variant: 'default' };
    const u = destructiveAction.user;
    const target = `${u.first_name} ${u.last_name} (${u.email})`;
    switch (destructiveAction.kind) {
      case 'reset-password':
        return { title: 'Reset password admin master', description: `Invia un'email di reset password a ${target} e invalida le sue sessioni.`, cta: 'Invia reset', variant: 'default' };
      case 'disable-2fa':
        return { title: 'Disattiva 2FA', description: `Disattiva il TOTP di ${target}. L'utente potrà rientrare senza 2FA finché non lo riattiva.`, cta: 'Disattiva 2FA', variant: 'destructive' };
      case 'revoke-sessions':
        return { title: 'Revoca tutte le sessioni', description: `Termina tutte le sessioni attive di ${target}.`, cta: 'Revoca sessioni', variant: 'destructive' };
      case 'delete-user':
        return { title: 'Elimina admin master', description: `Soft-delete di ${target}. Le sessioni vengono revocate. Azione tracciata in audit log.`, cta: 'Elimina utente', variant: 'destructive' };
    }
  }

  const dInfo = destructiveTitle();

  return (
    <div>
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Admin master</h1>
            <p className="text-muted-foreground">
              Utenti, ruoli e permessi del piano di controllo master · {users.length} utenti · {roles.length} ruoli · {catalog?.total ?? 0} permessi
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="flex justify-between items-center">
              <span>{error}</span>
              <button onClick={() => setError('')} className="text-xs underline">chiudi</button>
            </AlertDescription>
          </Alert>
        )}
        {successMessage && (
          <Alert className="mb-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200">{successMessage}</AlertDescription>
          </Alert>
        )}

        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
          <TabsList className="grid w-full grid-cols-4 max-w-2xl mb-6">
            <TabsTrigger value="users"><Users className="h-4 w-4 mr-1.5" />Utenti master</TabsTrigger>
            <TabsTrigger value="roles"><ShieldCheck className="h-4 w-4 mr-1.5" />Ruoli</TabsTrigger>
            <TabsTrigger value="permissions"><KeyRound className="h-4 w-4 mr-1.5" />Permessi</TabsTrigger>
            <TabsTrigger value="assignments"><Link2 className="h-4 w-4 mr-1.5" />Assegnazioni</TabsTrigger>
          </TabsList>

          {/* ============================== USERS ============================== */}
          <TabsContent value="users" className="space-y-4">
            {/* Task #2655 — Toggle "2FA obbligatorio" piano master. */}
            <GlassCard>
              <GlassCardContent className="py-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 mt-0.5 text-purple-600 dark:text-purple-400 shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">2FA obbligatorio per gli admin master</p>
                      <p className="text-sm text-muted-foreground">
                        Quando attivo, gli amministratori master non ancora iscritti dovranno
                        configurare l'autenticazione a due fattori al prossimo accesso.
                      </p>
                    </div>
                  </div>
                  <Switch
                    aria-label="2FA obbligatorio per gli admin master"
                    checked={twoFactorRequired}
                    disabled={twoFaReqLoading || twoFaReqSaving}
                    onCheckedChange={(v) => { void handleToggleTwoFaRequirement(v); }}
                  />
                </div>
              </GlassCardContent>
            </GlassCard>
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 lg:col-span-7">
                <GlassCard>
                  <GlassCardHeader>
                    <div className="flex justify-between items-center gap-3">
                      <GlassCardTitle>Admin master ({usersTotal})</GlassCardTitle>
                      <Button size="sm" onClick={() => { setCreateForm(EMPTY_CREATE_FORM); setCreateError(null); setShowCreateUser(true); }}>
                        <Plus className="h-4 w-4 mr-1" />Nuovo
                      </Button>
                    </div>
                    <div className="mt-2 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input className="pl-10" placeholder="Cerca per nome o email..."
                        value={usersSearch} onChange={(e) => setUsersSearch(e.target.value)} />
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <select
                        aria-label="Filtro ruolo"
                        className="px-2 py-1.5 text-sm border border-border dark:border-slate-600 rounded-md bg-white dark:bg-muted text-foreground"
                        value={filterRole}
                        onChange={(e) => { setPageOffset(0); setFilterRole(e.target.value as MasterUserRole | ''); }}
                      >
                        {ROLE_OPTIONS.map((r) => (
                          <option key={r || 'all-roles'} value={r}>{r === '' ? 'Tutti i ruoli' : r}</option>
                        ))}
                      </select>
                      <select
                        aria-label="Filtro stato"
                        className="px-2 py-1.5 text-sm border border-border dark:border-slate-600 rounded-md bg-white dark:bg-muted text-foreground"
                        value={filterStatus}
                        onChange={(e) => { setPageOffset(0); setFilterStatus(e.target.value as MasterUserStatus | ''); }}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s || 'all-status'} value={s}>{s === '' ? 'Tutti gli stati' : s}</option>
                        ))}
                      </select>
                      <select
                        aria-label="Filtro 2FA"
                        className="px-2 py-1.5 text-sm border border-border dark:border-slate-600 rounded-md bg-white dark:bg-muted text-foreground"
                        value={filterTotp}
                        onChange={(e) => { setPageOffset(0); setFilterTotp(e.target.value as '' | 'true' | 'false'); }}
                      >
                        <option value="">2FA: tutti</option>
                        <option value="true">2FA: attivo</option>
                        <option value="false">2FA: disattivo</option>
                      </select>
                    </div>
                  </GlassCardHeader>
                  <GlassCardContent>
                    {usersLoading ? <SkeletonTable rows={6} /> : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b dark:border-slate-700">
                              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Utente</th>
                              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Ruolo</th>
                              <th className="text-left py-2 px-2 font-medium text-muted-foreground">2FA</th>
                              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Stato</th>
                              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Ultimo accesso</th>
                              <th className="text-right py-2 px-2 font-medium text-muted-foreground">Azioni</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users.length === 0 ? (
                              <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">Nessun utente master</td></tr>
                            ) : users.map((u) => (
                              <tr key={u.id}
                                  className={`border-b dark:border-slate-700 hover:bg-muted/50 dark:hover:bg-white/5 cursor-pointer ${selectedUserId === u.id ? 'bg-cyan-50 dark:bg-cyan-900/20' : ''}`}
                                  onClick={() => setSelectedUserId(u.id)}>
                                <td className="py-2 px-2">
                                  <div className="flex items-center gap-2">
                                    <UserAvatar firstName={u.first_name} lastName={u.last_name} email={u.email} size="sm" />
                                    <div className="min-w-0">
                                      <div className="font-medium text-foreground truncate">{u.first_name} {u.last_name}</div>
                                      <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="py-2 px-2"><RoleBadge role={displayRole(u.role_id, u.role)} variant="master" /></td>
                                <td className="py-2 px-2"><TwoFactorStatusBadge enabled={u.totp_enabled} /></td>
                                <td className="py-2 px-2">
                                  <Badge variant={u.status === 'active' ? 'default' : 'secondary'}>{u.status}</Badge>
                                </td>
                                <td className="py-2 px-2 text-xs text-muted-foreground whitespace-nowrap">{formatDate(u.last_login_at)}</td>
                                <td className="py-2 px-2 text-right">
                                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditUser(u); }} title="Modifica">
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setDestructiveAction({ kind: 'delete-user', user: u }); setDestructiveError(null); }} title="Elimina"
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20">
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="flex items-center justify-between mt-3 px-1 text-xs text-muted-foreground">
                          <span>
                            {usersTotal === 0
                              ? 'Nessun risultato'
                              : `Risultati ${pageOffset + 1}–${pageEnd} di ${usersTotal}`}
                          </span>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" disabled={!canPrev}
                              onClick={() => setPageOffset(Math.max(0, pageOffset - PAGE_SIZE))}>
                              ← Precedente
                            </Button>
                            <Button size="sm" variant="outline" disabled={!canNext}
                              onClick={() => setPageOffset(pageOffset + PAGE_SIZE)}>
                              Successiva →
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </GlassCardContent>
                </GlassCard>
              </div>

              <div className="col-span-12 lg:col-span-5">
                <GlassCard>
                  <GlassCardHeader>
                    <GlassCardTitle>Dettaglio utente</GlassCardTitle>
                    <GlassCardDescription>
                      {selectedUserDetail ? `${selectedUserDetail.email}` : 'Seleziona un utente per vedere sessioni e azioni'}
                    </GlassCardDescription>
                  </GlassCardHeader>
                  <GlassCardContent>
                    {!selectedUserId ? (
                      <div className="text-center py-10 text-muted-foreground">
                        <Eye className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        Nessun utente selezionato
                      </div>
                    ) : userDetailLoading || !selectedUserDetail ? (
                      <SkeletonTable rows={4} />
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <UserAvatar firstName={selectedUserDetail.first_name} lastName={selectedUserDetail.last_name} email={selectedUserDetail.email} size="lg" />
                          <div className="min-w-0">
                            <div className="font-semibold text-foreground">{selectedUserDetail.first_name} {selectedUserDetail.last_name}</div>
                            <div className="text-sm text-muted-foreground truncate">{selectedUserDetail.email}</div>
                            <div className="mt-1 flex flex-wrap gap-1">
                              <RoleBadge role={displayRole(selectedUserDetail.role_id, selectedUserDetail.role)} variant="master" />
                              <TwoFactorStatusBadge enabled={selectedUserDetail.totp_enabled} />
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 text-xs">
                          <div><span className="text-muted-foreground">Ultimo login</span><div>{formatDate(selectedUserDetail.last_login_at)}</div></div>
                          <div><span className="text-muted-foreground">Auth provider</span><div>{selectedUserDetail.auth_provider}</div></div>
                          <div><span className="text-muted-foreground">Mobile</span><div>{selectedUserDetail.mobile ?? '—'}</div></div>
                          <div><span className="text-muted-foreground">Pwd change</span><div>{formatDate(selectedUserDetail.password_changed_at)}</div></div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" onClick={() => { setDestructiveAction({ kind: 'reset-password', user: selectedUserDetail }); setDestructiveError(null); }}>
                            <Key className="h-4 w-4 mr-1" />Reset password
                          </Button>
                          <Button size="sm" variant="outline" disabled={!selectedUserDetail.totp_enabled}
                            onClick={() => { setDestructiveAction({ kind: 'disable-2fa', user: selectedUserDetail }); setDestructiveError(null); }}>
                            <ShieldOff className="h-4 w-4 mr-1" />Disattiva 2FA
                          </Button>
                          <Button size="sm" variant="outline"
                            onClick={() => { setDestructiveAction({ kind: 'revoke-sessions', user: selectedUserDetail }); setDestructiveError(null); }}>
                            <LogOut className="h-4 w-4 mr-1" />Revoca tutte
                          </Button>
                        </div>

                        <div>
                          <div className="text-xs font-semibold text-muted-foreground mb-2">
                            Sessioni attive ({selectedUserDetail.sessions.length})
                          </div>
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {selectedUserDetail.sessions.length === 0 ? (
                              <div className="text-xs text-muted-foreground italic">Nessuna sessione attiva</div>
                            ) : selectedUserDetail.sessions.map((s: MasterRefreshTokenInfo) => (
                              <SessionRow key={s.id} session={s} onRevoke={(id) => void revokeSingleSession(id)} />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </GlassCardContent>
                </GlassCard>
              </div>
            </div>
          </TabsContent>

          {/* ============================== ROLES ============================== */}
          <TabsContent value="roles" className="space-y-4">
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 lg:col-span-6">
                <GlassCard>
                  <GlassCardHeader>
                    <div className="flex justify-between items-center">
                      <GlassCardTitle>Ruoli master ({roles.length})</GlassCardTitle>
                      <Button size="sm" onClick={openCreateRole}>
                        <Plus className="h-4 w-4 mr-1" />Nuovo ruolo
                      </Button>
                    </div>
                  </GlassCardHeader>
                  <GlassCardContent>
                    {rolesLoading ? <SkeletonTable rows={4} /> : (
                      <div className="space-y-2">
                        {roles.length === 0 ? (
                          <div className="text-center py-6 text-muted-foreground">Nessun ruolo</div>
                        ) : roles.map((r) => (
                          <div key={r.id}
                               className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedRoleId === r.id ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' : 'border-border hover:bg-muted/50'}`}
                               onClick={() => setSelectedRoleId(r.id)}>
                            <div className="flex justify-between items-start">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-foreground">{r.name}</span>
                                  {r.is_system && <Badge variant="outline" className="text-[10px]">system</Badge>}
                                  {r.role_kind && <Badge variant="secondary" className="text-[10px]">{r.role_kind}</Badge>}
                                </div>
                                {r.description && <div className="text-xs text-muted-foreground mt-0.5">{r.description}</div>}
                                <div className="text-xs text-muted-foreground mt-1">{r.user_count} utenti</div>
                              </div>
                              <div className="flex gap-1 shrink-0">
                                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditRole(r); }} title="Modifica" disabled={r.is_system}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setDeleteRoleError(null); setDeleteRoleTarget(r); }} title="Elimina"
                                  disabled={r.is_system}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </GlassCardContent>
                </GlassCard>
              </div>

              <div className="col-span-12 lg:col-span-6">
                <GlassCard>
                  <GlassCardHeader>
                    <GlassCardTitle>Dettaglio ruolo</GlassCardTitle>
                    <GlassCardDescription>
                      {selectedRoleDetail ? `${selectedRoleDetail.permissions.length} permessi · ${selectedRoleDetail.user_count} utenti` : 'Seleziona un ruolo a sinistra'}
                    </GlassCardDescription>
                  </GlassCardHeader>
                  <GlassCardContent>
                    {!selectedRoleId ? (
                      <div className="text-center py-10 text-muted-foreground">Nessun ruolo selezionato</div>
                    ) : roleDetailLoading || !selectedRoleDetail ? (
                      <SkeletonTable rows={4} />
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <div className="text-lg font-semibold">{selectedRoleDetail.name}</div>
                          {selectedRoleDetail.description && (
                            <div className="text-sm text-muted-foreground">{selectedRoleDetail.description}</div>
                          )}
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-muted-foreground mb-1.5">Permessi attivi</div>
                          {selectedRoleDetail.permissions.length === 0 ? (
                            <div className="text-xs text-muted-foreground italic">Nessun permesso assegnato</div>
                          ) : (
                            <div className="flex flex-wrap gap-1.5 max-h-72 overflow-y-auto">
                              {selectedRoleDetail.permissions.map((p) => (
                                <Badge key={p.id} variant="secondary" className="text-[10px]" title={p.description ?? ''}>
                                  {p.code}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button size="sm" variant="outline" onClick={() => { setAssignmentRoleId(selectedRoleDetail.id); setActiveTab('assignments'); }}>
                          <Link2 className="h-4 w-4 mr-1" />Gestisci assegnazioni
                        </Button>
                      </div>
                    )}
                  </GlassCardContent>
                </GlassCard>
              </div>
            </div>
          </TabsContent>

          {/* ============================== PERMISSIONS ============================== */}
          <TabsContent value="permissions" className="space-y-4">
            <GlassCard>
              <GlassCardHeader>
                <div className="flex justify-between items-center gap-3">
                  <div>
                    <GlassCardTitle>Catalogo permessi ({filteredCatalog?.total ?? 0})</GlassCardTitle>
                    <GlassCardDescription>Read-only · seed in master.permissions, raggruppati per category</GlassCardDescription>
                  </div>
                  <div className="w-72 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input className="pl-10" placeholder="Cerca permesso..." value={permSearch} onChange={(e) => setPermSearch(e.target.value)} />
                  </div>
                </div>
              </GlassCardHeader>
              <GlassCardContent>
                {catalogLoading ? <SkeletonTable rows={6} /> : !filteredCatalog || Object.keys(filteredCatalog.groups).length === 0 ? (
                  <div className="text-center py-6 text-muted-foreground">Nessun permesso</div>
                ) : (
                  <div className="space-y-5">
                    {Object.entries(filteredCatalog.groups).map(([cat, perms]) => (
                      <div key={cat}>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge>{cat}</Badge>
                          <span className="text-xs text-muted-foreground">{perms.length} permessi</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {perms.map((p) => (
                            <div key={p.id} className="p-2 rounded border border-border dark:border-slate-700 text-xs">
                              <div className="font-mono font-medium text-foreground">{p.code}</div>
                              <div className="text-muted-foreground">{p.name}</div>
                              {p.description && <div className="text-muted-foreground mt-1 line-clamp-2">{p.description}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </GlassCardContent>
            </GlassCard>
          </TabsContent>

          {/* ============================== ASSIGNMENTS ============================== */}
          <TabsContent value="assignments" className="space-y-4">
            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle>Assegnazioni ruolo → permessi</GlassCardTitle>
                <GlassCardDescription>
                  Seleziona un ruolo e spunta i permessi da assegnare. Il salvataggio applica un replace atomico (diff added/removed).
                </GlassCardDescription>
              </GlassCardHeader>
              <GlassCardContent>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Label className="shrink-0">Ruolo:</Label>
                  <select className="px-3 py-2 border border-border rounded-md bg-background min-w-[240px]"
                          value={assignmentRoleId} onChange={(e) => setAssignmentRoleId(e.target.value)}>
                    <option value="">— seleziona —</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}{r.is_system ? ' (system)' : ''}</option>
                    ))}
                  </select>
                  <div className="flex-1" />
                  {assignmentRoleId && (
                    <>
                      <span className="text-xs text-muted-foreground">
                        +{assignmentDiff.added.length} / −{assignmentDiff.removed.length}
                      </span>
                      <Button size="sm" variant="outline" onClick={() => void loadRoleAssignments(assignmentRoleId)} disabled={assignmentLoading || assignmentSubmitting}>
                        Reset
                      </Button>
                      <Button size="sm" onClick={() => setAssignmentConfirmOpen(true)}
                              disabled={assignmentSubmitting || (assignmentDiff.added.length === 0 && assignmentDiff.removed.length === 0)}
                              className="bg-cyan-600 hover:bg-cyan-700">
                        {assignmentSubmitting ? 'Salvataggio...' : 'Salva...'}
                      </Button>
                    </>
                  )}
                </div>

                {!assignmentRoleId ? (
                  <div className="text-center py-10 text-muted-foreground">Seleziona un ruolo per configurare i permessi</div>
                ) : assignmentLoading || !catalog ? (
                  <SkeletonTable rows={6} />
                ) : (
                  <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                    {Object.entries(catalog.groups).map(([cat, perms]) => {
                      const allChecked = perms.every((p) => assignmentSelectedIds.has(p.id));
                      const someChecked = perms.some((p) => assignmentSelectedIds.has(p.id));
                      return (
                        <div key={cat} className="border border-border dark:border-slate-700 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={allChecked ? true : someChecked ? 'indeterminate' : false}
                                onCheckedChange={(checked) => {
                                  setAssignmentSelectedIds((prev) => {
                                    const next = new Set(prev);
                                    if (checked) perms.forEach((p) => next.add(p.id));
                                    else perms.forEach((p) => next.delete(p.id));
                                    return next;
                                  });
                                }}
                              />
                              <Badge>{cat}</Badge>
                              <span className="text-xs text-muted-foreground">{perms.length} permessi</span>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5 pl-6">
                            {perms.map((p) => (
                              <label key={p.id} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-muted/50 rounded p-1">
                                <Checkbox checked={assignmentSelectedIds.has(p.id)} onCheckedChange={() => togglePermission(p.id)} />
                                <div className="min-w-0">
                                  <div className="font-mono text-foreground">{p.code}</div>
                                  <div className="text-muted-foreground">{p.name}</div>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </GlassCardContent>
            </GlassCard>
          </TabsContent>
        </Tabs>
      </div>

      {/* CREATE USER MODAL */}
      <Dialog open={showCreateUser} onOpenChange={(o) => { if (!o && !createSubmitting) setShowCreateUser(false); }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Nuovo admin master</DialogTitle>
            <DialogDescription>
              L'utente riceverà un'email per impostare la propria password al primo accesso.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome *</Label>
                <Input value={createForm.first_name} onChange={(e) => setCreateForm((f) => ({ ...f, first_name: e.target.value }))} />
              </div>
              <div><Label>Cognome *</Label>
                <Input value={createForm.last_name} onChange={(e) => setCreateForm((f) => ({ ...f, last_name: e.target.value }))} />
              </div>
            </div>
            <div><Label>Email *</Label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ruolo *</Label>
                <select className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        value={createForm.role_id} onChange={(e) => setCreateForm((f) => ({ ...f, role_id: e.target.value }))}>
                  <option value="">— seleziona —</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div><Label>Stato</Label>
                <select className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        value={createForm.status} onChange={(e) => setCreateForm((f) => ({ ...f, status: e.target.value as MasterUserStatus }))}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                  <option value="locked">locked</option>
                  <option value="pending_verification">pending_verification</option>
                </select>
              </div>
            </div>
            <div><Label>Mobile (opzionale)</Label>
              <Input value={createForm.mobile} onChange={(e) => setCreateForm((f) => ({ ...f, mobile: e.target.value }))} />
            </div>
            {createError && (
              <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{createError}</AlertDescription></Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)} disabled={createSubmitting}>Annulla</Button>
            <Button onClick={() => void submitCreateUser()} disabled={createSubmitting || !createForm.email || !createForm.first_name || !createForm.last_name || !createForm.role_id}
                    className="bg-cyan-600 hover:bg-cyan-700">
              {createSubmitting ? 'Creazione...' : 'Crea utente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT USER MODAL */}
      <Dialog open={editUserTarget !== null} onOpenChange={(o) => { if (!o && !editUserSubmitting) setEditUserTarget(null); }}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Modifica admin master</DialogTitle>
            <DialogDescription>{editUserTarget?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome</Label>
                <Input value={editUserForm.first_name ?? ''} onChange={(e) => setEditUserForm((f) => ({ ...f, first_name: e.target.value }))} />
              </div>
              <div><Label>Cognome</Label>
                <Input value={editUserForm.last_name ?? ''} onChange={(e) => setEditUserForm((f) => ({ ...f, last_name: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Ruolo</Label>
                <select className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        value={editUserForm.role_id ?? ''}
                        onChange={(e) => setEditUserForm((f) => ({ ...f, role_id: e.target.value || null }))}>
                  <option value="">— seleziona —</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div><Label>Stato</Label>
                <select className="w-full px-3 py-2 border border-border rounded-md bg-background"
                        value={editUserForm.status ?? ''} onChange={(e) => setEditUserForm((f) => ({ ...f, status: e.target.value as MasterUserStatus }))}>
                  <option value="active">active</option>
                  <option value="inactive">inactive</option>
                  <option value="locked">locked</option>
                  <option value="pending_verification">pending_verification</option>
                </select>
              </div>
            </div>
            {editUserError && (
              <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{editUserError}</AlertDescription></Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserTarget(null)} disabled={editUserSubmitting}>Annulla</Button>
            <Button onClick={() => void submitEditUser()} disabled={editUserSubmitting || !editUserForm.role_id} className="bg-cyan-600 hover:bg-cyan-700">
              {editUserSubmitting ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ROLE EDITOR MODAL */}
      <Dialog open={showRoleEditor !== null} onOpenChange={(o) => { if (!o && !roleSubmitting) setShowRoleEditor(null); }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{showRoleEditor?.mode === 'edit' ? 'Modifica ruolo' : 'Nuovo ruolo'}</DialogTitle>
            <DialogDescription>
              {showRoleEditor?.mode === 'edit' ? 'Aggiorna nome, descrizione o tipologia. I permessi si gestiscono nel tab Assegnazioni.' : 'Crea un nuovo ruolo. Potrai assegnare i permessi nel tab Assegnazioni.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label>
              <Input value={roleForm.name} onChange={(e) => setRoleForm((f) => ({ ...f, name: e.target.value }))} />
            </div>
            <div><Label>Descrizione</Label>
              <Textarea rows={2} value={roleForm.description} onChange={(e) => setRoleForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
            <div><Label>Tipologia</Label>
              <Input placeholder="es: support, devops, finance..." value={roleForm.role_kind}
                     onChange={(e) => setRoleForm((f) => ({ ...f, role_kind: e.target.value }))} />
            </div>
            {roleError && (
              <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>{roleError}</AlertDescription></Alert>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleEditor(null)} disabled={roleSubmitting}>Annulla</Button>
            <Button onClick={() => void submitRole()} disabled={roleSubmitting || !roleForm.name.trim()} className="bg-cyan-600 hover:bg-cyan-700">
              {roleSubmitting ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE ROLE CONFIRM */}
      <ConfirmActionModal
        open={deleteRoleTarget !== null}
        title="Elimina ruolo"
        description={deleteRoleTarget ? `Elimini definitivamente il ruolo "${deleteRoleTarget.name}". Gli utenti che lo usano restano senza ruolo granulare.` : ''}
        confirmLabel="Elimina"
        confirmVariant="destructive"
        requireReason={false}
        submitting={deleteRoleSubmitting}
        errorMessage={deleteRoleError}
        onConfirm={() => submitDeleteRole()}
        onCancel={() => setDeleteRoleTarget(null)}
      />

      {/* DESTRUCTIVE USER ACTION MODAL */}
      <ConfirmActionModal
        open={destructiveAction !== null}
        title={dInfo.title}
        description={dInfo.description}
        confirmLabel={dInfo.cta}
        confirmVariant={dInfo.variant}
        requireReason
        submitting={destructiveSubmitting}
        errorMessage={destructiveError}
        onConfirm={(reason) => runDestructive(reason)}
        onCancel={() => setDestructiveAction(null)}
      />

      {/* ASSIGNMENT DIFF CONFIRM — mostra +X/−Y con preview dei codici permesso */}
      <Dialog open={assignmentConfirmOpen} onOpenChange={(o) => { if (!o && !assignmentSubmitting) setAssignmentConfirmOpen(false); }}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Conferma modifica assegnazioni</DialogTitle>
            <DialogDescription>
              Replace atomico per il ruolo{' '}
              <strong>{roles.find((r) => r.id === assignmentRoleId)?.name ?? assignmentRoleId}</strong>.
              Verifica il diff prima di applicare.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[55vh] overflow-y-auto">
            <div className="flex items-center gap-3 text-sm">
              <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                +{assignmentDiff.added.length} aggiunti
              </Badge>
              <Badge className="bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                −{assignmentDiff.removed.length} rimossi
              </Badge>
              <span className="text-xs text-muted-foreground ml-auto">
                Totale finale: {assignmentSelectedIds.size}
              </span>
            </div>
            {assignmentDiff.added.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-green-700 dark:text-green-400 mb-1">Aggiunti</div>
                <ul className="space-y-1 pl-3 border-l-2 border-green-300 dark:border-green-700">
                  {assignmentDiff.added.map((id) => {
                    const p = permissionsById.get(id);
                    return (
                      <li key={id} className="text-xs">
                        <span className="font-mono text-foreground">{p?.code ?? id}</span>
                        {p?.name && <span className="text-muted-foreground"> · {p.name}</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {assignmentDiff.removed.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-red-700 dark:text-red-400 mb-1">Rimossi</div>
                <ul className="space-y-1 pl-3 border-l-2 border-red-300 dark:border-red-700">
                  {assignmentDiff.removed.map((id) => {
                    const p = permissionsById.get(id);
                    return (
                      <li key={id} className="text-xs">
                        <span className="font-mono text-foreground line-through">{p?.code ?? id}</span>
                        {p?.name && <span className="text-muted-foreground"> · {p.name}</span>}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignmentConfirmOpen(false)} disabled={assignmentSubmitting}>
              Annulla
            </Button>
            <Button onClick={() => void submitAssignment()} disabled={assignmentSubmitting} className="bg-cyan-600 hover:bg-cyan-700">
              {assignmentSubmitting ? 'Salvataggio...' : 'Applica modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
